/**
 * The theme of a chapter's game (docs/PATH-V2.md §4.1 p.4, §12.4, §13.1; review round 2): a game step of the path
 * names its chapter's idea (`PathGameSetup.themeFamily`: 'reach', 'gifts', 'save', 'trade', 'check', 'routine'), and
 * «Учитель» opens that game with it — one line of `v3.path.game.<family>` (content lines.gameTheme.ts) instead of the
 * opening card's family (`lessonGameStart` with `pathTheme`: the card is not announced, so its family reminders do not
 * follow either; the card still shapes the arrows). The badge «Тема: …» says the chapter's idea too.
 *
 * Pure: the caller says the event ('pathLesson', `say: [{ pool, n }]` — the recorded wording) and keeps nothing.
 */
import type { CoachEvent } from '@gambit/shared';
import { GAME_THEME_BADGE_RU, PATH_GAME_THEME_LINES, gameThemePool, isGameThemeFamily } from '@gambit/content';
import type { Rng } from '../phrase.ts';

/** The chapter's theme of a game, or null (not a chapter's theme — an exam, an ordinary game, an unknown family). */
export function pathGameTheme(family: string | undefined): string | null {
  return isGameThemeFamily(family) ? family : null;
}

/** «Тема: …» of a chapter's game (a label, never spoken); null when `family` is not a chapter's theme. */
export function pathGameThemeBadge(family: string | undefined): string | null {
  return isGameThemeFamily(family) ? GAME_THEME_BADGE_RU[family] : null;
}

/** The start line of a chapter's game: one wording of its pool (by `rng`), or null when `family` has none. */
export function pathGameThemeEvent(family: string | undefined, ply: number, rng: Rng = Math.random): CoachEvent | null {
  if (!isGameThemeFamily(family)) return null;
  const pool = gameThemePool(family);
  const all = PATH_GAME_THEME_LINES[pool] ?? [];
  if (all.length === 0) return null;
  const i = Math.min(all.length - 1, Math.max(0, Math.floor(rng() * all.length)));
  const text = (all[i] as { t: string }).t;
  return { id: `path-theme-${family}-${ply}`, kind: 'pathLesson', priority: 1, text, bubbleText: text, pose: 'talk', pauseClock: true, say: [{ pool, n: i + 1 }] };
}
